import { useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useUserUiPrefs } from '../hooks/useUserUiPrefs';
import { isThemeId } from '../lib/themes';

/**
 * ThemeSync — bridges the server-backed per-user theme pref to ThemeProvider.
 * Phase 3 of docs/backlog/theming-system.md.
 *
 * ThemeProvider lives ABOVE SessionProvider (so the lobby/auth screens are themed
 * too), but the server pref rail (useUserUiPrefs) needs SessionContext — which is
 * below. This component is mounted INSIDE the in-session tree and wires the two:
 *   - server → local: a theme chosen on another device (authoritative) is applied;
 *   - local → server: a user-driven change is persisted (dormant until a selector
 *     exists — Phase 3 keeps it hidden while only one theme ships).
 *
 * Theme rides the freeform `uiPreferences` JSONB under the `theme` key; the server
 * shallow-merges arbitrary keys, so no backend change was needed. Renders nothing.
 */
const PREF_KEY = 'theme';

export function ThemeSync() {
  const { theme, setTheme } = useTheme();
  const { prefs, setPrefs, ready } = useUserUiPrefs();
  const serverTheme = prefs[PREF_KEY];

  // server → local (cross-device authoritative choice)
  useEffect(() => {
    if (isThemeId(serverTheme) && serverTheme !== theme) {
      setTheme(serverTheme);
    }
  }, [serverTheme, theme, setTheme]);

  // local → server (persist; converges in one write — server echoes back equal).
  // Gated on `ready`: до ответа ui-prefs-get prefs = {} и немедленная запись
  // затирала бы тему, выбранную на другом девайсе, раньше, чем мы её увидим.
  useEffect(() => {
    if (ready && serverTheme !== theme) {
      setPrefs({ [PREF_KEY]: theme });
    }
  }, [ready, theme, serverTheme, setPrefs]);

  return null;
}
